import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import { useEffect } from 'react';
import H4 from 'UIComponents/elements/headings/H4';
import UIFlex from 'UIComponents/layout/UIFlex';
import { EditPlaylistButton, PlaylistCreatedAndAssignedByLabel, useFullPlaylist } from '..';
import { useAllTracks } from '../../tracks';
import styled, { theme, useMediaQuery } from 'academy-common-lib/utils/Styling';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UICopyButton from 'UIComponents/button/UICopyButton';
import StartPlaylistButton from './StartPlaylistButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import H7 from 'UIComponents/elements/headings/H7';
import UsageTracker from '../../../utils/UsageTracker';
import { SCREENS } from '../../../constants/Tracking';
import { useUserOwnsPlaylist } from '../../savedItems';
import { UserAttributeKeys, useUserAttribute } from '../../userAttributes';
import { AssignPlaylistButton } from '../../playlistAssignments';
import I18n from 'I18n';
import { HasGate } from '../../accounts';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PlaylistBreakdownList = styled(UIList).withConfig({
  displayName: "PlaylistDetails__PlaylistBreakdownList",
  componentId: "sc-1uiicd3-0"
})(["&& > li{padding-left:0;}"]);
const PlaylistBreakdownListItem = styled.div.withConfig({
  displayName: "PlaylistDetails__PlaylistBreakdownListItem",
  componentId: "sc-1uiicd3-1"
})(["display:flex;align-items:center;margin-right:12px;&& > span{margin-right:8px;}"]);
const IncludesHeading = styled(H7).withConfig({
  displayName: "PlaylistDetails__IncludesHeading",
  componentId: "sc-1uiicd3-2"
})(["margin-bottom:8px;"]);
const PlaylistActions = styled(UIFlex).attrs({
  gap: 12
}).withConfig({
  displayName: "PlaylistDetails__PlaylistActions",
  componentId: "sc-1uiicd3-3"
})(["margin-bottom:16px;"]);
const CertificationsIcon = styled(_ref => {
  let props = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/_jsx(UIIcon, Object.assign({
    color: theme.colors.EERIE,
    name: "certification",
    size: "xs"
  }, props));
}).withConfig({
  displayName: "PlaylistDetails__CertificationsIcon",
  componentId: "sc-1uiicd3-4"
})(["position:relative;top:4px;"]);
const usePlaylistBreakdown = playlistItems => {
  const {
    allTracks
  } = useAllTracks();
  const breakdown = {
    certifications: 0,
    lessons: 0,
    shorts: 0
  };
  for (const playlistItem of playlistItems) {
    if (playlistItem.type === 'LESSON') {
      breakdown.lessons++;
    } else if (playlistItem.type === 'TRACK') {
      const track = allTracks[playlistItem.itemId];
      if (track && track.lessonIds) breakdown.lessons += track.lessonIds.length;
      if (track && track.certification) {
        breakdown.certifications++;
      }
    } else if (playlistItem.type === 'SHORT_FORM_CONTENT') {
      breakdown.shorts++;
    }
  }
  return breakdown;
};
const PlaylistBreakdown = ({
  playlistItems
}) => {
  const breakdown = usePlaylistBreakdown(playlistItems);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(IncludesHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.savedItems.playlistDetails.includes"
      })
    }), /*#__PURE__*/_jsxs(PlaylistBreakdownList, {
      inline: true,
      children: [/*#__PURE__*/_jsxs(PlaylistBreakdownListItem, {
        children: [/*#__PURE__*/_jsx(CertificationsIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.numberOfCertifications",
          options: {
            count: breakdown.certifications
          }
        })]
      }), /*#__PURE__*/_jsxs(PlaylistBreakdownListItem, {
        children: [/*#__PURE__*/_jsx(UIIcon, {
          color: theme.colors.EERIE,
          name: "presentation",
          size: "xs"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.numberOfLessons",
          options: {
            count: breakdown.lessons
          }
        })]
      }), /*#__PURE__*/_jsx(HasGate, {
        gate: "Academy:ShortFormContent",
        children: /*#__PURE__*/_jsxs(PlaylistBreakdownListItem, {
          children: [/*#__PURE__*/_jsx(UIIcon, {
            color: theme.colors.EERIE,
            name: "video",
            size: "xs",
            style: {
              transform: 'translateY(2px)'
            }
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.savedItems.playlistDetails.numberOfShorts",
            options: {
              count: breakdown.shorts
            }
          })]
        })
      })]
    })]
  });
};
export const PlaylistDetails = ({
  playlistId
}) => {
  const {
    isDesktop,
    isMobile
  } = useMediaQuery();
  const {
    playlist
  } = useFullPlaylist(playlistId);
  const userIsOwner = useUserOwnsPlaylist(playlistId);
  const {
    setUserAttribute
  } = useUserAttribute(UserAttributeKeys.HAS_VIEWED_PLAYLIST);
  useEffect(() => {
    UsageTracker.track('pageView', {
      screen: SCREENS.PLAYLIST
    });
  }, []);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setUserAttribute(true);
  }, [setUserAttribute]);
  if (!playlist) return null;
  const trackPlaylistShare = () => {
    UsageTracker.track('playlistInteraction', {
      action: 'share-playlist',
      isOwner: userIsOwner,
      playlistId,
      location: 'playlist-details'
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(UIFlex, {
      direction: "row",
      justify: "between",
      children: [/*#__PURE__*/_jsx(H4, {
        children: playlist.name
      }), /*#__PURE__*/_jsx(EditPlaylistButton, {
        $isHiddenFor: "mobile",
        playlistId: playlistId
      })]
    }), /*#__PURE__*/_jsxs(PlaylistActions, {
      direction: isDesktop ? 'row' : 'column',
      children: [/*#__PURE__*/_jsx(StartPlaylistButton, {
        appearsIn: "playlist",
        block: isMobile,
        playlistId: playlistId,
        showLoader: true,
        style: {
          flex: 1
        }
      }), /*#__PURE__*/_jsxs(UIFlex, {
        gap: 12,
        style: {
          flex: 0,
          width: isMobile ? '100%' : undefined
        },
        children: [/*#__PURE__*/_jsx(UICopyButton, {
          block: isMobile,
          hintSuccess: I18n.text('learningCenter.shareThis.notification'),
          onClick: trackPlaylistShare,
          tooltipPlacement: "bottom",
          use: "secondary",
          value: window.location.href,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.common.share"
          })
        }), /*#__PURE__*/_jsx(AssignPlaylistButton, {
          block: isMobile,
          className: "m-top-0 m-left-0",
          playlistId: playlistId,
          use: "secondary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.playlistAssignments.assign"
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(PlaylistCreatedAndAssignedByLabel, {
      className: "m-bottom-4",
      playlistId: playlistId
    }), /*#__PURE__*/_jsx("p", {
      children: playlist.description
    }), /*#__PURE__*/_jsx(PlaylistBreakdown, {
      playlistItems: playlist.items
    })]
  });
};